import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Alert,
    Button,
    Card,
    Descriptions,
    Empty,
    List,
    Popconfirm,
    Skeleton,
    Space,
    Tag,
    Typography,
    message,
} from "antd";
import {
    ArrowLeftOutlined,
    AuditOutlined,
    BranchesOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    HistoryOutlined,
    MailOutlined,
    ReloadOutlined,
    StopOutlined,
} from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";

import agentGovernanceApi from "../../../api/v3/agentGovernanceApi";
import {
    failContract,
    formatContractError,
    requireArray,
    requireRecord,
    requireTimezoneAwareDateTime,
} from "../../mailHub/inbox/models/contractValidation";
import MailReadingSurface from "../../mailHub/inbox/components/MailReadingSurface";
import { getReviewStatus, mapDecisionResultStatus } from "./AgReviewStatusContract";
import "./AgReviewPage.css";

const { Text, Title } = Typography;

const OPEN_REVIEW_STATUSES = new Set(["open", "under_review"]);

const PROPOSAL_TYPE_LANGUAGE = {
    "mail_hub.business_link": "邮件业务关联建议",
    "mail_hub.semantic_edge": "邮件语义关系建议",
    "schedule_draft.flight_plan": "航班计划建议",
};

const SOURCE_DOMAIN_LANGUAGE = {
    mail_hub: "Mail Hub 邮件",
    schedule_draft: "航班计划草稿",
    operation: "航班运行",
};

const ENTITY_TYPE_LANGUAGE = {
    flight_operation: "航班",
    flight: "航班",
    schedule: "航班计划",
    settlement_invoice: "账单",
    ap_invoice: "账单",
    invoice: "账单",
};

const DECISION_ACTIONS = [
    {
        key: "accept_as_is",
        label: "采纳",
        buttonType: "primary",
        icon: <CheckCircleOutlined />,
        confirmTitle: "确认采纳该判断？",
        confirmDescription: "采纳后系统会按当前提案尝试应用对应业务影响。",
        successText: "已提交采纳决定",
    },
    {
        key: "reject",
        label: "驳回",
        danger: true,
        icon: <CloseCircleOutlined />,
        confirmTitle: "确认驳回该判断？",
        confirmDescription: "驳回表示当前判断不成立，不会应用这些业务影响。",
        successText: "已提交驳回决定",
    },
    {
        key: "ignore",
        label: "放弃",
        icon: <StopOutlined />,
        confirmTitle: "确认放弃本次审核？",
        confirmDescription: "放弃表示本次判断不再处理，但不代表邮件事实错误。",
        successText: "已提交放弃决定",
    },
];

function extractResponseData(response) {
    return response?.data?.data ?? response?.data ?? null;
}

function toArray(value) {
    return Array.isArray(value) ? value : [];
}

function isRecord(value) {
    return Boolean(value && typeof value === "object" && !Array.isArray(value));
}

function toRecord(value) {
    return isRecord(value) ? value : {};
}

function normalizeText(value, fallback = "") {
    if (value === null || value === undefined) return fallback;
    const text = String(value).replace(/\s+/g, " ").trim();
    return text || fallback;
}

function formatShortId(value) {
    const text = normalizeText(value);
    if (!text) return "";
    return text.length > 10 ? `${text.slice(0, 6)}…${text.slice(-4)}` : text;
}

function formatTime(value) {
    return requireTimezoneAwareDateTime(value, "reviewCase.audit_time").format("YYYY-MM-DD HH:mm");
}

function getProposalTypeLabel(reviewCase) {
    const proposalType = normalizeText(reviewCase?.proposal_type || reviewCase?.proposal_payload?.proposal_type);
    if (PROPOSAL_TYPE_LANGUAGE[proposalType]) return PROPOSAL_TYPE_LANGUAGE[proposalType];
    if (proposalType.startsWith("mail_hub")) return "邮件业务判断建议";
    return "业务判断建议";
}

function getSourceDomainLabel(reviewCase) {
    const sourceDomain = normalizeText(reviewCase?.source_domain).toLowerCase();
    return SOURCE_DOMAIN_LANGUAGE[sourceDomain] || "业务来源";
}

function getEntityTypeLabel(entityType) {
    const key = normalizeText(entityType).toLowerCase();
    return ENTITY_TYPE_LANGUAGE[key] || null;
}

function pushRef(refs, ref) {
    if (!isRecord(ref)) return;
    refs.push(ref);
    toArray(ref.related_refs).forEach((item) => pushRef(refs, item));
}

function collectEffectLines(reviewCase) {
    const candidates = [
        ...toArray(reviewCase?.expected_effects),
        ...toArray(reviewCase?.proposal_payload?.effect_lines),
        ...toArray(reviewCase?.review_payload?.effect_lines),
    ];
    const seen = new Set();
    const lines = [];

    candidates.forEach((line, index) => {
        if (!isRecord(line)) return;
        const key = normalizeText(line.effect_id) || `${normalizeText(line.effect_type)}:${index}`;
        if (seen.has(key)) return;
        seen.add(key);
        lines.push(line);
    });

    return lines;
}

function extractMailRefs(reviewCase, effectLines) {
    const refs = [];
    pushRef(refs, reviewCase?.source_ref);
    effectLines.forEach((line) => {
        pushRef(refs, line.source_ref);
        pushRef(refs, line.target_ref);
        pushRef(refs, line.business_object_ref);
        const payload = toRecord(line.payload);
        if (payload.source_mailbox_receipt_id || payload.mailbox_receipt_id) {
            refs.push({ domain: "mail_hub", type: "mailbox_receipt", id: payload.source_mailbox_receipt_id || payload.mailbox_receipt_id });
        }
        if (payload.canonical_message_id) {
            refs.push({ domain: "mail_hub", type: "canonical_message", id: payload.canonical_message_id });
        }
        if (payload.thread_id) {
            refs.push({ domain: "mail_hub", type: "thread", id: payload.thread_id });
        }
    });

    const receiptRef = refs.find((ref) => normalizeText(ref.domain) === "mail_hub" && ["mailbox_receipt", "receipt"].includes(normalizeText(ref.type)));
    const threadRef = refs.find((ref) => normalizeText(ref.domain) === "mail_hub" && ["thread", "mail_thread"].includes(normalizeText(ref.type)));
    const canonicalRef = refs.find((ref) => normalizeText(ref.domain) === "mail_hub" && normalizeText(ref.type) === "canonical_message");

    return {
        receiptId: normalizeText(receiptRef?.id),
        threadId: normalizeText(threadRef?.id),
        canonicalMessageId: normalizeText(canonicalRef?.id),
    };
}

function extractEffectEntity(line) {
    const payload = toRecord(line.payload);
    const businessObjectRef = toRecord(line.business_object_ref);
    const targetRef = toRecord(line.target_ref);
    const entityType = normalizeText(payload.entity_type || businessObjectRef.type || line.entity_type);
    const entityId = normalizeText(payload.entity_id || businessObjectRef.id || line.entity_id);
    const fallbackType = normalizeText(targetRef.domain) === "operation" ? normalizeText(targetRef.type) : "";
    const fallbackId = normalizeText(targetRef.domain) === "operation" ? normalizeText(targetRef.id) : "";

    if (entityType && entityId) {
        return { entityType, entityId };
    }
    if (fallbackType && fallbackId) {
        return { entityType: fallbackType, entityId: fallbackId };
    }
    return null;
}

function collectEffectEntities(effectLines) {
    const byKey = new Map();
    effectLines.forEach((line) => {
        const entity = extractEffectEntity(line);
        if (!entity) return;
        const key = `${entity.entityType}:${entity.entityId}`;
        byKey.set(key, {
            ...entity,
            label: `${getEntityTypeLabel(entity.entityType) || "未知业务对象类型"} ${formatShortId(entity.entityId)}`,
        });
    });
    return Array.from(byKey.values());
}

function getEffectType(line) {
    const effectType = normalizeText(line.effect_type || line.operation).toLowerCase();
    if (effectType.includes("business_link")) return { label: "Business Link", color: "blue" };
    if (effectType.includes("semantic")) return { label: "语义边", color: "purple" };
    if (effectType.includes("conflict")) return { label: "冲突说明", color: "volcano" };
    return { label: "业务影响", color: "default" };
}

function getEffectDescription(line) {
    const payload = toRecord(line.payload);
    const metadata = toRecord(payload.metadata);
    return normalizeText(
        payload.description
        || metadata.match_reasoning
        || metadata.reason
        || line.description
        || line.summary,
        "当前提案未返回更详细的影响说明。",
    );
}

function buildEffectViewModels(effectLines) {
    return effectLines.map((line, index) => {
        const entity = extractEffectEntity(line);
        return {
            key: normalizeText(line.effect_id) || String(index),
            type: getEffectType(line),
            entityLabel: entity ? `${getEntityTypeLabel(entity.entityType) || "未知业务对象类型"} ${formatShortId(entity.entityId)}` : "业务对象待确认",
            description: getEffectDescription(line),
        };
    });
}

function buildConflictNotes(reviewCase) {
    const notes = [];
    const status = normalizeText(reviewCase?.status).toLowerCase();
    const validation = toRecord(reviewCase?.validation_result);
    const preconditions = toArray(reviewCase?.preconditions);

    if (status === "precondition_failed") {
        notes.push("相关业务事实已经变化，需要重新判断或重新发起审核。");
    }
    if (status === "application_failed") {
        notes.push("审核已完成，但业务结果应用失败，需要重新处理应用结果。");
    }
    if (validation.valid === false) {
        notes.push("当前提案未通过自动校验，需要人工确认风险。");
    }
    if (preconditions.length > 0) {
        notes.push(`采纳前需要满足 ${preconditions.length} 项业务前置条件。`);
    }

    return notes;
}

function buildFailureReasons(decisionResult, reviewCase) {
    const outcome = toRecord(decisionResult?.outcome);
    const diff = toRecord(outcome.diff);
    const reasons = [diff.reason, diff.error, diff.message, diff.failure_reason]
        .map((item) => normalizeText(item))
        .filter(Boolean);
    const status = normalizeText(outcome.status || reviewCase?.status).toLowerCase();

    if (reasons.length > 0) return reasons;
    if (["application_failed", "precondition_failed"].includes(status)) {
        return ["当前响应未返回更具体的失败原因。"];
    }
    return [];
}

function getDecisionErrorMessage(error) {
    const status = error?.response?.status;
    if (status === 403) return "当前账号没有裁决权限。";
    if (status === 409) return "审核内容已变化，请刷新后再处理。";
    if (status === 422) return "裁决内容不符合当前审核要求。";
    return "提交裁决失败，请稍后重试。";
}

function getBindingItems(bindings) {
    return toArray(bindings).map((binding) => ({
        key: normalizeText(binding.id || binding.review_case_id || `${binding.entity_type}:${binding.entity_id}`),
        label: `${getEntityTypeLabel(binding.entity_type) || "未知业务对象类型"} ${formatShortId(binding.entity_id)}`,
        summary: normalizeText(binding.review_case_summary?.summary, "已形成审核投影"),
    }));
}

function validateAgReviewContract(reviewCase, effectEntities, bindings, decisionResult) {
    if (!reviewCase) return "";

    if (!getReviewStatus(reviewCase.status)) {
        failContract("AG 审核页契约错误", `reviewCase.status 返回未知枚举 ${normalizeText(reviewCase.status) || "<empty>"}`);
    }
    requireTimezoneAwareDateTime(reviewCase.created_at, "reviewCase.created_at");
    requireTimezoneAwareDateTime(reviewCase.updated_at, "reviewCase.updated_at");

    effectEntities.forEach((entity) => {
        if (!getEntityTypeLabel(entity.entityType)) {
            failContract("AG 审核页契约错误", `effect entity_type 返回未知枚举 ${entity.entityType || "<empty>"}`);
        }
    });

    bindings.forEach((binding) => {
        if (!getEntityTypeLabel(binding.entity_type)) {
            failContract("AG 审核页契约错误", `binding.entity_type 返回未知枚举 ${binding.entity_type || "<empty>"}`);
        }
    });

    if (decisionResult) {
        const decisionStatus = decisionResult.outcome?.status || decisionResult.application?.status || decisionResult.review_case_status;
        if (!mapDecisionResultStatus(decisionStatus)) {
            failContract("AG 审核页契约错误", `decision result status 返回未知枚举 ${normalizeText(decisionStatus) || "<empty>"}`);
        }
    }
    return "";
}

function getAvailableActions(reviewCase) {
    return new Set(toArray(reviewCase?.visibility?.available_actions).map((item) => normalizeText(item)));
}

export default function AgReviewPage() {
    const params = useParams();
    const navigate = useNavigate();
    const reviewCaseId = params.reviewCaseId || params.review_case_id || "";
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [reviewCase, setReviewCase] = useState(null);
    const [bindings, setBindings] = useState([]);
    const [decisionResult, setDecisionResult] = useState(null);
    const [submitting, setSubmitting] = useState("");

    const loadBindings = useCallback(async (caseDetail) => {
        const effectEntities = collectEffectEntities(collectEffectLines(caseDetail));
        const entityGroups = effectEntities.reduce((groups, entity) => {
            const current = groups.get(entity.entityType) || [];
            current.push(entity.entityId);
            groups.set(entity.entityType, current);
            return groups;
        }, new Map());

        if (entityGroups.size === 0) return [];

        const responses = await Promise.all(Array.from(entityGroups.entries()).map(([entityType, entityIds]) => (
            agentGovernanceApi.listEntityBindings({ entityType, entityIds, bindingStatus: "all", limit: 200 })
        )));

        return responses
            .flatMap((response, index) => requireArray(requireRecord(extractResponseData(response), `entityBindingsResponse[${index}]`).bindings, `entityBindingsResponse[${index}].bindings`))
            .filter((binding) => normalizeText(binding.review_case_id) === normalizeText(caseDetail.id));
    }, []);

    const loadData = useCallback(async ({ showLoading = true } = {}) => {
        if (!reviewCaseId) {
            setError("缺少审核案编号，无法加载审核工作台。");
            return;
        }
        if (showLoading) setLoading(true);
        setError("");

        try {
            const response = await agentGovernanceApi.getReviewCase(reviewCaseId);
            const caseDetail = requireRecord(extractResponseData(response), "reviewCase");
            const bindingItems = await loadBindings(caseDetail);
            setReviewCase(caseDetail);
            setBindings(bindingItems);
        } catch (requestError) {
            console.error("加载 AG 审核案失败", requestError);
            setReviewCase(null);
            setBindings([]);
            setError(requestError?.response?.status === 404 ? "未找到该审核案。" : formatContractError(requestError, "加载审核案失败，请稍后重试。"));
        } finally {
            if (showLoading) setLoading(false);
        }
    }, [loadBindings, reviewCaseId]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const effectLines = useMemo(() => collectEffectLines(reviewCase), [reviewCase]);
    const effectEntities = useMemo(() => collectEffectEntities(effectLines), [effectLines]);
    const effectItems = useMemo(() => buildEffectViewModels(effectLines), [effectLines]);
    const mailRefs = useMemo(() => extractMailRefs(reviewCase, effectLines), [reviewCase, effectLines]);
    const conflictNotes = useMemo(() => buildConflictNotes(reviewCase), [reviewCase]);
    const bindingItems = useMemo(() => getBindingItems(bindings), [bindings]);
    const failureReasons = useMemo(() => buildFailureReasons(decisionResult, reviewCase), [decisionResult, reviewCase]);
    const availableActions = useMemo(() => getAvailableActions(reviewCase), [reviewCase]);
    const contractError = useMemo(() => {
        try {
            return validateAgReviewContract(reviewCase, effectEntities, bindings, decisionResult);
        } catch (validationError) {
            return formatContractError(validationError, "AG 审核页契约错误");
        }
    }, [bindings, decisionResult, effectEntities, reviewCase]);
    const currentStatus = getReviewStatus(reviewCase?.status);
    const isReviewOpen = OPEN_REVIEW_STATUSES.has(normalizeText(reviewCase?.status).toLowerCase());
    const focusEntity = effectEntities[0]
        ? { entity_type: effectEntities[0].entityType, entity_id: effectEntities[0].entityId }
        : null;

    const submitDecision = useCallback(async (decisionType) => {
        if (!reviewCase || submitting) return;
        setSubmitting(decisionType);
        try {
            const payload = {
                proposal_revision_id: reviewCase.proposal_revision_id,
                proposal_payload_hash: reviewCase.payload_hash,
                grounding_snapshot_hash: reviewCase.grounding_snapshot_hash,
                review_payload_hash: reviewCase.review_payload_hash,
                decision_type: decisionType,
                decision_payload: {},
            };
            const response = await agentGovernanceApi.submitDecision(reviewCase.id, payload);
            const result = requireRecord(extractResponseData(response), "decisionResult");
            setDecisionResult(result);
            const action = DECISION_ACTIONS.find((item) => item.key === decisionType);
            message.success(action?.successText || "已提交审核决定");
            await loadData({ showLoading: false });
        } catch (requestError) {
            console.error("提交 AG 审核决定失败", requestError);
            message.error(formatContractError(requestError, getDecisionErrorMessage(requestError)));
        } finally {
            setSubmitting("");
        }
    }, [loadData, reviewCase, submitting]);

    if (loading && !reviewCase) {
        return (
            <div className="page-container ag-review-page">
                <Skeleton active paragraph={{ rows: 12 }} />
            </div>
        );
    }

    if (error) {
        return (
            <div className="page-container ag-review-page">
                <Alert
                    type="error"
                    showIcon
                    message="审核工作台不可用"
                    description={error}
                    action={<Button icon={<ReloadOutlined />} onClick={() => loadData()}>重新加载</Button>}
                />
            </div>
        );
    }

    if (!reviewCase) {
        return (
            <div className="page-container ag-review-page">
                <Empty description="未找到审核案" />
            </div>
        );
    }

    if (contractError) {
        return (
            <div className="page-container ag-review-page">
                <Alert
                    type="error"
                    showIcon
                    message="审核工作台契约错误"
                    description={contractError}
                    action={<Button icon={<ReloadOutlined />} onClick={() => loadData()}>重新加载</Button>}
                />
            </div>
        );
    }

    return (
        <div className="page-container ag-review-page">
            <div className="ag-review-hero">
                <Space direction="vertical" size={10} className="ag-review-hero-main">
                    <Space wrap>
                        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>返回</Button>
                        <Tag color="blue">{getSourceDomainLabel(reviewCase)}</Tag>
                        <Tag color={currentStatus.color}>{currentStatus.label}</Tag>
                    </Space>
                    <Title level={3} className="ag-review-title">
                        <AuditOutlined /> 审核与处置工作台
                    </Title>
                    <Text type="secondary">{normalizeText(reviewCase.summary, "系统已形成一项待确认判断。")}</Text>
                </Space>
                <Button icon={<ReloadOutlined />} onClick={() => loadData()} loading={loading}>刷新</Button>
            </div>

            <Card className="ag-review-card" title="审核对象头部">
                <Descriptions bordered size="small" column={{ xs: 1, sm: 1, md: 2 }}>
                    <Descriptions.Item label="提案类型">{getProposalTypeLabel(reviewCase)}</Descriptions.Item>
                    <Descriptions.Item label="当前状态"><Tag color={currentStatus.color}>{currentStatus.label}</Tag></Descriptions.Item>
                    <Descriptions.Item label="涉及业务对象" span={2}>
                        {effectEntities.length > 0 ? (
                            <Space wrap>{effectEntities.map((entity) => <Tag key={`${entity.entityType}:${entity.entityId}`}>{entity.label}</Tag>)}</Space>
                        ) : (
                            <Text type="secondary">当前审核详情未返回业务对象。</Text>
                        )}
                    </Descriptions.Item>
                    <Descriptions.Item label="来源邮件" span={2}>
                        {mailRefs.receiptId || mailRefs.threadId ? (
                            <Space wrap>
                                <Tag color="geekblue">已定位邮件证据</Tag>
                                <Text type="secondary">邮件阅读面会加载对应邮件和 Thread 背景。</Text>
                            </Space>
                        ) : mailRefs.canonicalMessageId ? (
                            <Text type="secondary">当前仅返回邮件主体引用，暂缺可直接读取的收据或 Thread 定位。</Text>
                        ) : (
                            <Text type="secondary">当前审核详情未返回来源邮件定位。</Text>
                        )}
                    </Descriptions.Item>
                </Descriptions>
            </Card>

            <div className="ag-review-grid">
                <section className="ag-review-evidence">
                    <Card className="ag-review-card" title={<><MailOutlined /> 邮件证据区</>}>
                        <MailReadingSurface
                            entry="review"
                            threadId={mailRefs.threadId}
                            receiptId={mailRefs.receiptId}
                            focusEntity={focusEntity}
                            reviewCaseId={reviewCase.id}
                        />
                    </Card>
                </section>

                <aside className="ag-review-side">
                    <Card className="ag-review-card" title={<><BranchesOutlined /> 业务影响区</>}>
                        {effectItems.length > 0 ? (
                            <List
                                dataSource={effectItems}
                                renderItem={(item) => (
                                    <List.Item className="ag-review-effect-item">
                                        <Space direction="vertical" size={6}>
                                            <Space wrap>
                                                <Tag color={item.type.color}>{item.type.label}</Tag>
                                                <Text strong>{item.entityLabel}</Text>
                                            </Space>
                                            <Text type="secondary">{item.description}</Text>
                                        </Space>
                                    </List.Item>
                                )}
                            />
                        ) : (
                            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="当前审核详情未返回业务影响。" />
                        )}

                        <div className="ag-review-conflict-block">
                            <Text strong>冲突说明</Text>
                            {conflictNotes.length > 0 ? (
                                <ul className="ag-review-note-list">
                                    {conflictNotes.map((note) => <li key={note}>{note}</li>)}
                                </ul>
                            ) : (
                                <Text type="secondary">当前详情未返回冲突说明。</Text>
                            )}
                        </div>

                        {bindingItems.length > 0 ? (
                            <div className="ag-review-binding-block">
                                <Text strong>关联投影</Text>
                                {bindingItems.map((item) => (
                                    <div key={item.key} className="ag-review-binding-item">
                                        <Tag>{item.label}</Tag>
                                        <Text type="secondary">{item.summary}</Text>
                                    </div>
                                ))}
                            </div>
                        ) : null}
                    </Card>

                    <Card className="ag-review-card" title="决策区">
                        <Space direction="vertical" size={12} className="ag-review-decision-block">
                            {!isReviewOpen ? (
                                <Alert type="info" showIcon message="此审核已处理，当前只能查看证据与审计信息。" />
                            ) : availableActions.size === 0 ? (
                                <Alert type="warning" showIcon message="当前账号暂无裁决权限或该审核暂不可裁决。" />
                            ) : null}

                            <Space wrap>
                                {DECISION_ACTIONS.map((action) => {
                                    const enabled = isReviewOpen && availableActions.has(action.key);
                                    return (
                                        <Popconfirm
                                            key={action.key}
                                            title={action.confirmTitle}
                                            description={action.confirmDescription}
                                            okText="确认"
                                            cancelText="取消"
                                            onConfirm={() => submitDecision(action.key)}
                                            disabled={!enabled}
                                        >
                                            <Button
                                                type={action.buttonType}
                                                danger={action.danger}
                                                icon={action.icon}
                                                disabled={!enabled}
                                                loading={submitting === action.key}
                                            >
                                                {action.label}
                                            </Button>
                                        </Popconfirm>
                                    );
                                })}
                            </Space>
                        </Space>
                    </Card>

                    <Card className="ag-review-card" title={<><HistoryOutlined /> 历史与审计</>}>
                        <Descriptions size="small" column={1} bordered>
                            <Descriptions.Item label="创建时间">{formatTime(reviewCase.created_at)}</Descriptions.Item>
                            <Descriptions.Item label="最近更新">{formatTime(reviewCase.updated_at)}</Descriptions.Item>
                            <Descriptions.Item label="当前结果"><Tag color={currentStatus.color}>{currentStatus.label}</Tag></Descriptions.Item>
                            {decisionResult ? (
                                <Descriptions.Item label="本次提交">
                                    {mapDecisionResultStatus(decisionResult.outcome?.status || decisionResult.application?.status || decisionResult.review_case_status)}
                                </Descriptions.Item>
                            ) : null}
                        </Descriptions>

                        {failureReasons.length > 0 ? (
                            <Alert
                                className="ag-review-audit-alert"
                                type="warning"
                                showIcon
                                message="失败原因"
                                description={failureReasons.join("；")}
                            />
                        ) : null}

                        {!decisionResult ? (
                            <Text type="secondary" className="ag-review-muted-line">
                                当前 AG 详情暂未返回历史裁决明细，页面仅展示审核案状态与时间摘要。
                            </Text>
                        ) : null}
                    </Card>
                </aside>
            </div>
        </div>
    );
}
